import { useState, type FormEvent } from "react";
import Layout from "@/components/feature/Layout";
import JsonLd from "@/components/feature/JsonLd";
import Breadcrumb from "@/components/feature/Breadcrumb";
import usePageMeta from "@/hooks/usePageMeta";
import { siteInfo } from "@/mocks/archive";

type FormStatus = "idle" | "submitting" | "success" | "error";

type FormResponse = {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
};

const MAX_MESSAGE = 500;
const FORM_ENDPOINT = "https://readdy.ai/api/form/daiiv4jp14a1h3mr74vg";

export default function Contact() {
  const [status, setStatus] = useState<FormStatus>("idle");
  const [formError, setFormError] = useState("");
  const [messageLength, setMessageLength] = useState(0);

  usePageMeta({
    title: "联系我们｜溯影档案 · 提交黑料网红考古线索与内容修订",
    description:
      "向溯影档案提交黑料网红考古线索、旧言论材料或内容修订说明。请附上可核对的公开链接，我们一般 2 个工作日内回复，仅处理公开信息。",
    keywords: "联系溯影档案,提交黑料线索,黑料网红考古,内容修订,线索投稿",
    canonical: "https://scadaforweb.cn/contact",
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("company_alt") ?? "").trim();
    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "company_alt") return;
      if (typeof value === "string" && value.trim()) payload.append(key, value);
    });

    if (honeypot) {
      setStatus("success");
      setMessageLength(0);
      form.reset();
      return;
    }

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      const isOk = response.ok && parsed?.code === "OK" && !isSpam;

      if (isOk) {
        setStatus("success");
        setMessageLength(0);
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg.trim()
            ? serverMsg
            : "提交没有成功，请稍后再试一次。"
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络连接出现问题，请检查网络后重新提交。");
    }
  };

  return (
    <Layout>
      <JsonLd
        id="ld-contact"
        data={{
          "@context": "https://schema.org",
          "@type": "ContactPage",
          name: "联系溯影档案",
          description: "提交黑料网红考古线索、内容修订说明与转载合作事宜的页面。",
          url: "https://scadaforweb.cn/contact",
          inLanguage: "zh-CN",
          breadcrumb: {
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://scadaforweb.cn/" },
              { "@type": "ListItem", position: 2, name: "联系我们", item: "https://scadaforweb.cn/contact" },
            ],
          },
          mainEntity: {
            "@type": "Organization",
            name: siteInfo.brand,
            email: siteInfo.email,
            contactPoint: {
              "@type": "ContactPoint",
              contactType: "customer support",
              email: siteInfo.email,
              availableLanguage: ["zh-CN"],
            },
          },
        }}
      />

      <section className="w-full px-4 md:px-8 pt-8 md:pt-10 pb-10 bg-background-100 border-b border-background-300">
        <Breadcrumb items={[{ name: "首页", to: "/" }, { name: "联系我们" }]} />
        <h1 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-snug">
          联系我们
        </h1>
        <p className="mt-4 max-w-3xl text-sm md:text-base text-foreground-600 leading-relaxed">
          发现了一条还没被整理的旧料？或者你认为某份档案的记录需要修正？填下面的表单告诉我们。
          请务必附上可公开访问的链接或出处，这会让核对快很多。
        </p>
      </section>

      <div className="w-full px-4 md:px-8 py-12 md:py-16 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
        <aside className="lg:col-span-4 space-y-5">
          <div className="p-5 md:p-6 rounded-lg bg-background-100 border border-background-300">
            <h2 className="font-heading text-lg font-bold text-foreground-950 mb-4">直接联络</h2>
            <ul className="space-y-3 text-sm text-foreground-700">
              <li className="flex items-start gap-2">
                <i className="ri-mail-line text-primary-500 mt-0.5"></i>
                <a href={`mailto:${siteInfo.email}`} className="hover:text-primary-600 transition-colors break-all">
                  {siteInfo.email}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-timer-line text-primary-500 mt-0.5"></i>
                <span>{siteInfo.replyTime}</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-map-pin-line text-primary-500 mt-0.5"></i>
                <span>{siteInfo.location}（线上协作）</span>
              </li>
            </ul>
          </div>

          <div className="p-5 md:p-6 rounded-lg bg-accent-50 border border-accent-200">
            <h2 className="font-heading text-base font-bold text-foreground-950 mb-3 flex items-center gap-2">
              <i className="ri-error-warning-line text-accent-700"></i>
              提交前请注意
            </h2>
            <ul className="space-y-2 text-sm text-foreground-700 leading-relaxed">
              <li>· 只提交公开发布过的内容与链接。</li>
              <li>· 请勿提交任何非公开的个人隐私信息。</li>
              <li>· 说明尽量写上时间、平台与已知出处。</li>
              <li>· 修订请求请附可核对的材料，便于快速处理。</li>
            </ul>
          </div>
        </aside>

        <div className="lg:col-span-8">
          <form
            id="contact-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="p-5 md:p-8 rounded-lg bg-background-100 border border-background-300 space-y-5"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-900 mb-2">
                  你的称呼 <span className="text-primary-500">*</span>
                </label>
                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  required
                  maxLength={40}
                  placeholder="怎么称呼你"
                  className="w-full px-4 py-2.5 text-sm rounded-md bg-background-50 border border-background-300 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400"
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-900 mb-2">
                  邮箱 <span className="text-primary-500">*</span>
                </label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  required
                  placeholder="用于接收核对结果"
                  className="w-full px-4 py-2.5 text-sm rounded-md bg-background-50 border border-background-300 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label htmlFor="contact-type" className="block text-sm font-medium text-foreground-900 mb-2">
                  事项类型
                </label>
                <select
                  id="contact-type"
                  name="type"
                  defaultValue="提供新线索"
                  className="w-full px-4 py-2.5 text-sm rounded-md bg-background-50 border border-background-300 text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400"
                >
                  <option value="提供新线索">提供新线索</option>
                  <option value="内容修订">内容修订</option>
                  <option value="转载与合作">转载与合作</option>
                  <option value="其他">其他</option>
                </select>
              </div>
              <div>
                <label htmlFor="contact-link" className="block text-sm font-medium text-foreground-900 mb-2">
                  相关公开链接
                </label>
                <input
                  id="contact-link"
                  name="link"
                  type="url"
                  placeholder="https://"
                  className="w-full px-4 py-2.5 text-sm rounded-md bg-background-50 border border-background-300 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-900">
                  详细说明 <span className="text-primary-500">*</span>
                </label>
                <span className="text-xs text-foreground-500">
                  {messageLength}/{MAX_MESSAGE}
                </span>
              </div>
              <textarea
                id="contact-message"
                name="message"
                required
                maxLength={MAX_MESSAGE}
                rows={6}
                onChange={(e) => setMessageLength(e.target.value.length)}
                placeholder="请描述这条线索的时间、平台、已知出处，以及你认为值得核对的点。"
                className="w-full px-4 py-3 text-sm rounded-md bg-background-50 border border-background-300 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 resize-y"
              ></textarea>
            </div>

            <div className="form-field-alt">
              <label htmlFor="contact-company-alt">公司名称</label>
              <input
                id="contact-company-alt"
                name="company_alt"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />
            </div>

            {status === "success" && (
              <div className="flex items-start gap-3 p-4 rounded-md bg-accent-100 border border-accent-300">
                <i className="ri-checkbox-circle-line text-accent-700 text-lg mt-0.5"></i>
                <p className="text-sm text-accent-900 leading-relaxed">
                  已收到你的提交，感谢帮忙补上这块缺口。我们会按流程核对来源，{siteInfo.replyTime}。
                </p>
              </div>
            )}

            {status === "error" && (
              <div className="flex items-start gap-3 p-4 rounded-md bg-primary-100 border border-primary-300">
                <i className="ri-error-warning-line text-primary-700 text-lg mt-0.5"></i>
                <p className="text-sm text-primary-900 leading-relaxed">
                  提交失败：{formError}
                </p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row items-center gap-3 pt-1">
              <button
                type="submit"
                disabled={status === "submitting"}
                className="w-full sm:w-auto px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {status === "submitting" ? "正在提交..." : "提交线索"}
              </button>
              <p className="text-xs text-foreground-500 leading-relaxed">
                提交即表示你确认所提供内容均为公开信息。
              </p>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
}